import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  Clock, MapPin, Users, Edit, Wallet, Copy, Trash2, GripVertical, Eye, Download 
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { CATEGORIES } from './constants';

// Action button component with label
const ActionButton = ({ onClick, icon: Icon, label, color, testId }) => (
  <button
    onClick={onClick}
    className={cn(
      "flex flex-col items-center gap-0.5 p-1.5 rounded-lg transition-colors",
      "text-slate-400 hover:bg-slate-700",
      color
    )}
    title={label}
    data-testid={testId}
  >
    <Icon className="w-4 h-4" />
    <span className="text-[9px] leading-tight">{label}</span>
  </button>
);

export const ActivityCard = ({ 
  item, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onViewMembers, 
  onViewPayments,
  onView,
  onExportPdf,
  isDragging, 
  formatCurrency 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: item?.id || 'temp' });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  if (!item) return null;

  const CategoryIcon = CATEGORIES[item.category]?.icon || MapPin;
  const categoryColor = CATEGORIES[item.category]?.color || 'bg-slate-500';

  return (
    <div ref={setNodeRef} style={style}>
      <Card className="bg-slate-800 border-slate-700 overflow-hidden">
        <CardContent className="p-0">
          <div className="flex">
            {/* Category Strip */}
            <div className={cn("w-1.5 flex-shrink-0", categoryColor)} />
            
            {/* Drag Handle */}
            <div
              {...attributes}
              {...listeners}
              className="flex items-center px-2 cursor-grab active:cursor-grabbing hover:bg-slate-700/50 transition-colors"
            >
              <GripVertical className="w-4 h-4 text-slate-500" />
            </div>
            
            <div className="flex-1 p-4 pl-2">
              {/* Content */}
              <div className="flex-1 min-w-0">
                {/* Time & Category */}
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  {item.start_time && (
                    <Badge variant="outline" className="text-xs text-slate-400 border-slate-600">
                      <Clock className="w-3 h-3 mr-1" />
                      {item.start_time}
                      {item.end_time && ` - ${item.end_time}`}
                    </Badge>
                  )}
                  <Badge className={cn("text-xs text-white", categoryColor)}>
                    <CategoryIcon className="w-3 h-3 mr-1" />
                    {CATEGORIES[item.category]?.label || 'Outro'}
                  </Badge>
                </div>

                {/* Title */}
                <h3 className="font-semibold text-white mb-1">{item.title}</h3>

                {/* Location */}
                {item.location && (
                  <p className="text-sm text-slate-400 flex items-center gap-1 mb-1">
                    <MapPin className="w-3 h-3" />
                    {item.location}
                  </p>
                )}

                {/* Description */}
                {item.description && (
                  <p className="text-sm text-slate-500 line-clamp-2">{item.description}</p>
                )}

                {/* Cost & Participants */}
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  {!item.is_free && item.cost > 0 && (
                    <span className="text-xs text-green-400 flex items-center gap-1">
                      {formatCurrency(item.cost)}
                    </span>
                  )}
                  <span className="text-xs text-slate-500 flex items-center gap-1">
                    <Users className="w-3 h-3" />
                    {item.is_for_everyone ? 'Todos' : `${item.assigned_members?.length || 0} participantes`}
                  </span>
                </div>
              </div>

              {/* Footer Actions - Centered */}
              <div className="flex items-center justify-center flex-wrap gap-1 mt-3 pt-3 border-t border-slate-700/50">
                <ActionButton
                  onClick={() => onView(item)}
                  icon={Eye}
                  label="Ver"
                  color="hover:text-cyan-400"
                  testId={`view-activity-${item.id}`}
                />
                <ActionButton
                  onClick={() => onEdit(item)}
                  icon={Edit}
                  label="Editar"
                  color="hover:text-white"
                  testId={`edit-activity-${item.id}`}
                />
                <ActionButton
                  onClick={() => onViewMembers(item)}
                  icon={Users}
                  label="Membros"
                  color="hover:text-orange-400"
                  testId={`members-activity-${item.id}`}
                />
                <ActionButton
                  onClick={() => onViewPayments(item)}
                  icon={Wallet}
                  label="Pagamento"
                  color="hover:text-green-400"
                  testId={`payments-activity-${item.id}`}
                />
                <ActionButton
                  onClick={() => onExportPdf(item)}
                  icon={Download}
                  label="PDF"
                  color="hover:text-purple-400"
                  testId={`export-pdf-activity-${item.id}`}
                />
                <ActionButton
                  onClick={() => onDuplicate(item)}
                  icon={Copy}
                  label="Copiar"
                  color="hover:text-blue-400"
                  testId={`duplicate-activity-${item.id}`}
                />
                <ActionButton
                  onClick={() => onDelete(item)}
                  icon={Trash2}
                  label="Excluir"
                  color="hover:text-red-400"
                  testId={`delete-activity-${item.id}`}
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
